import { useState } from 'react';
import { BellRing, Bell } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import { formatPrice } from '@/lib/formatters';

interface PriceAlertButtonProps {
  variantId: string;
  currentPrice: number | null;
}

/** Prisalarm: brugeren vælger en målpris og kan slå alarmen til/fra. */
export function PriceAlertButton({ variantId, currentPrice }: PriceAlertButtonProps) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<string>('');

  const { data: alert } = useQuery({
    queryKey: ['price-alert', userId, variantId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('price_alerts')
        .select('id, target_price_dkk, is_active')
        .eq('variant_id', variantId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const isActive = !!alert?.is_active;

  const save = useMutation({
    mutationFn: async (payload: { targetPrice: number | null; active: boolean }) => {
      if (!userId) throw new Error('login');
      const { error } = await supabase.from('price_alerts').upsert(
        {
          user_id: userId,
          variant_id: variantId,
          target_price_dkk: payload.targetPrice,
          is_active: payload.active,
        },
        { onConflict: 'user_id,variant_id' },
      );
      if (error) throw new Error(error.message);
    },
    onSuccess: (_d, payload) => {
      qc.invalidateQueries({ queryKey: ['price-alert', userId, variantId] });
      setOpen(false);
      toast({ title: payload.active ? 'Prisalarm oprettet' : 'Prisalarm slået fra' });
    },
    onError: (err: Error) => {
      toast({
        title: err.message === 'login' ? 'Log ind for at oprette prisalarm' : 'Kunne ikke gemme prisalarmen',
        variant: 'destructive',
      });
    },
  });

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={isActive ? 'default' : 'outline'}
        className="gap-2"
        onClick={() => {
          if (!userId) {
            toast({ title: 'Log ind for at oprette prisalarm', variant: 'destructive' });
            return;
          }
          setTarget(
            alert?.target_price_dkk
              ? String(alert.target_price_dkk)
              : currentPrice
                ? String(Math.round((currentPrice * 0.95) / 1000) * 1000)
                : '',
          );
          setOpen(true);
        }}
      >
        {isActive ? <BellRing className="w-4 h-4" aria-hidden /> : <Bell className="w-4 h-4" aria-hidden />}
        {isActive ? 'Prisalarm aktiv' : 'Prisalarm'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Prisalarm</DialogTitle>
            <DialogDescription>
              Vi noterer din målpris, så du kan følge, om bilen falder i pris.
              {currentPrice ? ` Nuværende pris: ${formatPrice(currentPrice)}.` : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="target-price">Målpris (kr.)</Label>
            <Input
              id="target-price"
              type="number"
              inputMode="numeric"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="fx 299000"
            />
          </div>

          <DialogFooter className="gap-2">
            {isActive && (
              <Button
                variant="outline"
                onClick={() => save.mutate({ targetPrice: alert?.target_price_dkk ?? null, active: false })}
                disabled={save.isPending}
              >
                Slå alarm fra
              </Button>
            )}
            <Button
              onClick={() =>
                save.mutate({ targetPrice: target ? Number(target) : null, active: true })
              }
              disabled={save.isPending}
            >
              Gem prisalarm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
